import type { EstimateProductResponse } from '../../../../core/models/estimate-rating';
import type { ServiceAvailabilityProduct } from '../../../../core/models/service-availability';
import type { EstimateOfferingInput } from './delivery-service-matrix';

/** Product Master fields needed for commitment labels (ESN productServiceInfo equivalent). */
export interface ProductMasterCommitment {
  productCode: string;
  /** Backend display label, e.g. "Guaranteed* by 9 AM" or "By End of Day". */
  serviceTimeEnglish?: string;
  /** Backend French display label, e.g. "Livraison Garantie* d'ici 9 h". */
  serviceTimeFrench?: string;
  descEn?: string;
  descFr?: string;
}

/**
 * Adds transit days to a ship date and returns YYYY/MM/DD.
 * Mirrors ESN transit-based delivery date construction when estimatedDeliveryDate is absent.
 */
export function addTransitDaysToShipDate(shipDate: string, transitDays: number): string {
  const normalized = shipDate.trim().replaceAll('/', '-');
  const [yearText, monthText, dayText] = normalized.split('-');
  const year = Number.parseInt(yearText ?? '', 10);
  const month = Number.parseInt(monthText ?? '', 10);
  const day = Number.parseInt(dayText ?? '', 10);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + Math.max(0, transitDays));

  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}`;
}

/**
 * Normalizes PSA date strings (YYYY-MM-DD or YYYY/MM/DD) to YYYY/MM/DD.
 */
function normalizeDeliveryDate(value: string): string {
  return value.trim().replaceAll('-', '/');
}

/**
 * Resolves delivery date from PSA product metadata (expected date or transitTime).
 * ESN used estimate.estimatedDeliveryDate from legacy rating; modern S4 has no date —
 * PSA transit / expectedDeliveryDate is the OLS source.
 */
export function resolveDeliveryDateFromPsaProduct(
  product: ServiceAvailabilityProduct,
  shipDate: string,
): string | null {
  const expected =
    (typeof product.expectedDeliveryDate === 'string' && product.expectedDeliveryDate) ||
    (typeof product.estimatedDeliveryDate === 'string' && product.estimatedDeliveryDate) ||
    null;

  if (expected) {
    return normalizeDeliveryDate(expected);
  }

  const transitRaw = product.transitTime ?? product.transitDays;
  const transitDays =
    typeof transitRaw === 'number'
      ? transitRaw
      : typeof transitRaw === 'string'
        ? Number.parseInt(transitRaw, 10)
        : Number.NaN;

  if (Number.isFinite(transitDays)) {
    return addTransitDaysToShipDate(shipDate, transitDays);
  }

  return null;
}

/**
 * Maps Product Master serviceTimeEnglish onto tokens consumed by resolveDeliveryTimeBucket.
 * Accepts raw or backend-prefixed labels (e.g. "Guaranteed* by 9 AM", "By End of Day").
 */
export function resolveDeliveryTimeFromProductMaster(
  product: Pick<ProductMasterCommitment, 'serviceTimeEnglish'> | undefined,
): string {
  const raw = (product?.serviceTimeEnglish ?? '').trim().toUpperCase();
  if (!raw) {
    return '17:00';
  }

  if (raw.includes('EVENING')) {
    return '21:00';
  }

  if (raw.includes('END OF DAY') || raw === 'STD' || raw.includes('EOD')) {
    return '17:00';
  }

  if (raw.includes('10:30') || raw.includes('1030')) {
    return '10:30';
  }

  if (raw.includes('12')) {
    return '12:00';
  }

  if (raw.includes('9') && !raw.includes('19') && !raw.includes('21')) {
    return '09:00';
  }

  return raw;
}

/**
 * Reads guaranteed status from the backend serviceTimeEnglish label (includes "Guaranteed*").
 */
export function resolveGuaranteedFromServiceTime(serviceTimeEnglish?: string): boolean {
  return (serviceTimeEnglish ?? '').toUpperCase().includes('GUARANTEED');
}

/**
 * True when Product Master names this courier SKU as a return service (EN Return / FR Retour).
 * ESO Lite outbound Service step does not offer return products.
 */
export function isReturnCourierProduct(product: { descEn?: string; descFr?: string }): boolean {
  const catalogName = `${product.descEn ?? ''} ${product.descFr ?? ''}`.toLowerCase();
  return /\breturn\b/.test(catalogName) || /\bretour\b/.test(catalogName);
}

/**
 * Joins S4 pricing with PSA dates and Product Master time/guarantee metadata by product code.
 * Products missing from PSA or from products/selection (product master) are omitted so the
 * matrix only shows selectable catalog offerings (avoids duplicate generic Express cards).
 * Commitment copy is taken as-is from serviceTimeEnglish / serviceTimeFrench.
 */
export function mergeDeliveryOfferings(input: {
  shipDate: string;
  psaProducts: ServiceAvailabilityProduct[];
  productMaster: ProductMasterCommitment[];
  estimates: EstimateProductResponse[];
}): EstimateOfferingInput[] {
  const psaByCode = new Map<string, ServiceAvailabilityProduct>();
  for (const product of input.psaProducts) {
    const code = String(product.productNumber ?? '').trim();
    if (code) {
      psaByCode.set(code, product);
    }
  }

  const masterByCode = new Map<string, ProductMasterCommitment>();
  for (const product of input.productMaster) {
    const code = String(product.productCode ?? '').trim();
    if (code) {
      masterByCode.set(code, product);
    }
  }

  const merged: EstimateOfferingInput[] = [];

  for (const estimate of input.estimates) {
    if (estimate.outcome !== 'RATING_SUCCESS' || !estimate.pricing) {
      continue;
    }

    const psa = psaByCode.get(estimate.productCode);
    if (!psa) {
      continue;
    }

    const deliveryDate = resolveDeliveryDateFromPsaProduct(psa, input.shipDate);
    if (!deliveryDate) {
      continue;
    }

    const master = masterByCode.get(estimate.productCode);
    if (!master || isReturnCourierProduct(master)) {
      continue;
    }

    const serviceTimeEnglish = master.serviceTimeEnglish?.trim() || undefined;
    const serviceTimeFrench = master.serviceTimeFrench?.trim() || undefined;

    merged.push({
      ...estimate,
      deliveryDate,
      deliveryTime: resolveDeliveryTimeFromProductMaster(master),
      serviceTimeEnglish,
      serviceTimeFrench,
      guaranteed: resolveGuaranteedFromServiceTime(serviceTimeEnglish),
      transportationMode: String(psa.transportationMode ?? 'A').trim() || 'A',
      descEn: master.descEn?.trim() || undefined,
      descFr: master.descFr?.trim() || undefined,
    });
  }

  return merged;
}
